import { useEffect, useReducer, useState } from 'react';
//Layout
import Header from 'layout/header/Header';
import Capsule from 'layout/Capsule';

//Components
import Button from 'react-bootstrap/Button';
import OffcanvasManageProduct from './components/OffcanvasManageProduct';
import { IoAdd, IoCamera, IoCheckmark, IoExpand } from 'react-icons/io5';
import PlaceHolderItemList from 'components/loader/PlaceHolderItemList';
import { toast } from 'react-toastify';
import OffcanvasManagePackage from './components/OffcanvasManagePackage';

//Utils
import axios from 'axios';

//Logic
import useOffCanvasReducer from 'hooks/useOffCanvasReducer';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { reorderArrayItem } from 'utils/helpers';

function useProducts() {

    const [products, setProducts] = useState();
    const [manageProduct, dispatchManageProduct] = useOffCanvasReducer();
    const [confirmType, setConfirmType] = useState(null);
    // Id dei pacchetti che contengono ESATTAMENTE gli stessi servizi di un altro pacchetto
    const [duplicatePackageIds, setDuplicatePackageIds] = useState(new Set());

    // Calcola quali pacchetti hanno lo stesso identico insieme di servizi inclusi.
    // Restituisce un Set con gli id dei pacchetti coinvolti (per mostrare il badge).
    const computeDuplicatePackages = (packages) => {
        const bySignature = {};
        (packages || []).forEach(pkg => {
            const ids = [...new Set((pkg.items || []).map(it => it.product_id))].sort((a, b) => a - b);
            if (ids.length === 0) return;
            const signature = ids.join(',');
            (bySignature[signature] = bySignature[signature] || []).push(pkg.id);
        });
        const duplicates = new Set();
        Object.values(bySignature).forEach(group => {
            if (group.length > 1) group.forEach(id => duplicates.add(id));
        });
        return duplicates;
    };

    const reloadDuplicatePackages = () => {
        axios.get('packages')
            .then(res => setDuplicatePackageIds(computeDuplicatePackages(res.data)))
            .catch(() => { });
    };


    const handleReorderProduct = (result, type) => {
        if (!result.destination) {
            return;
        }
        const newProducts = reorderArrayItem(products.filter(p => p.type === type), result.source.index, result.destination.index);
        axios.put('products-order', {
            products: newProducts.map(p => p.id)
        })
            .then(res => {
                toast.success('Ordine prodotti aggiornato con successo');
                setProducts([
                    ...products.filter(p => p.type !== type),
                    ...newProducts
                ]);
            });
    }

    const handleAddProduct = type => {
        // Primo click: chiedi conferma (evita insert accidentali di servizi vuoti).
        // Secondo click sullo stesso tipo: crea davvero il servizio.
        if (confirmType !== type) {
            setConfirmType(type);
            setTimeout(() => setConfirmType(c => (c === type ? null : c)), 4000);
            return;
        }
        setConfirmType(null);
        axios.post('products', {
            type
        })
            .then(res => {
                setProducts([
                    ...products,
                    res.data
                ]);
                toast.success('Prodotto aggiunto con successo, compilare di seguito i dati');
                dispatchManageProduct(res.data);
            });
    }

    const onUpdateProduct = product => {
        setProducts(products.map((p) => {
            if (p.id !== product.id) return p;
            return product;
        }));
    }

    // Crea il pacchetto (Product type = PACKAGE) e lo restituisce per aprire il modale
    const createPackage = async () => {
        return axios.post('products', { type: 'PACKAGE' })
            .then(res => {
                setProducts([...(products || []), res.data]);
                return res.data;
            });
    }

    // Rimuove il pacchetto dalla lista dopo l'eliminazione
    const removeProductFromList = (id) => {
        setProducts((products || []).filter(p => p.id !== id));
    }

    useEffect(() => {
        axios.get('products')
            .then(res => setProducts(res.data))
        reloadDuplicatePackages();
    }, []);

    return {
        products,
        manageProduct, dispatchManageProduct,
        handleReorderProduct,
        handleAddProduct,
        confirmType, setConfirmType,
        onUpdateProduct,
        createPackage,
        removeProductFromList,
        duplicatePackageIds,
        reloadDuplicatePackages
    }
}

function ProductItem({ products, product, onEditProduct, isSkeleton, isDuplicate }) {

    return (
        <div className="item">
            <div className="icon-box me-05">
                {!product.parent_id && <IoCamera />}
            </div>
            {isSkeleton ? <PlaceHolderItemList /> : <>
                <div className="in">
                    <div>
                        <span>
                            {product.parent_id && <>
                                <div className='text-active text-12 lh-1 mb-05'>{products.find(p => p.id === product.parent_id).description}</div>
                            </>}
                            {product.description}
                            {isDuplicate && (
                                <span className="badge bg-danger ms-1" style={{ fontSize: '10px', verticalAlign: 'middle' }}>
                                    Stessi servizi di un altro pacchetto
                                </span>
                            )}
                        </span>
                    </div>
                    <div style={{ minWidth: '120px', textAlign: 'right' }}>
                        <Button
                            size="sm"
                            className='btn-light'
                            onClick={() => onEditProduct(product)}
                        >
                            Modifica
                        </Button>
                        <IoExpand className='ms-1 cursor-drag' style={{ fontSize: '20px' }} />
                    </div>
                </div>
            </>}
        </div>
    );
}

function AddProduct({ onClick, title, isConfirming }) {
    return (
        <li className='cursor-pointer' onClick={onClick}>
            <div className="item">
                <div className={`icon-box ${isConfirming ? 'bg-warning' : 'bg-success'}`}>
                    {isConfirming ? <IoCheckmark /> : <IoAdd />}
                </div>
                <div className={`in ${isConfirming ? 'text-warning' : 'text-success'}`}>
                    <div className="fw-500">
                        {isConfirming ? 'Confermi? Clicca di nuovo per creare' : `Aggiungi servizi ${title}`}
                    </div>
                </div>
            </div>
        </li>
    );
}

function ProductSection({
    products,
    title,
    type,
    onEditProduct,
    handleReorderProduct,
    handleAddProduct = null,
    confirmType,
    isSkeleton,
    duplicateIds
}) {

    const getItemStyle = (isDragging, draggableStyle) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",

        // change background colour if dragging
        background: isDragging ? "#000" : "#292929",

        // styles we need to apply on draggables
        ...draggableStyle
    });

    return (
        <div className={`section ${isSkeleton ? 'no-pointer-events' : ''}`}>
            <div className="section-title">
                <h4 className="card-subtitle text-white mt-1 text-12">SERVIZI {title}:</h4>
            </div>
            <DragDropContext onDragEnd={result => handleReorderProduct(result, type)}>
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <ul
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="listview image-listview rounded"
                        >
                            {handleAddProduct && <AddProduct title={title} isConfirming={confirmType === type} onClick={() => !isSkeleton && handleAddProduct(type)} />}
                            {products && products.filter((p, index) => p.type === type || p.isSkeleton)
                                .map((product, index) => {
                                    return <Draggable key={index} draggableId={'p' + index} index={index}>
                                        {(provided, snapshot) => (
                                            <li
                                                key={product.id}
                                                className='rounded'
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={getItemStyle(
                                                    snapshot.isDragging,
                                                    provided.draggableProps.style
                                                )}
                                            >
                                                <ProductItem
                                                    key={product.id}
                                                    product={product}
                                                    onEditProduct={onEditProduct}
                                                    products={products}
                                                    isSkeleton={product.isSkeleton}
                                                    isDuplicate={duplicateIds?.has(product.id)}
                                                />
                                            </li>
                                        )}
                                    </Draggable>
                                })}
                            {provided.placeholder}
                        </ul>
                    )}
                </Droppable>
            </DragDropContext>

        </div>
    )
}

export default function Products() {

    const {
        products,
        manageProduct, dispatchManageProduct,
        handleReorderProduct,
        handleAddProduct,
        confirmType, setConfirmType,
        onUpdateProduct,
        createPackage,
        removeProductFromList,
        duplicatePackageIds,
        reloadDuplicatePackages
    } = useProducts();

    // Pacchetto attualmente in editing nel modale dedicato
    const [packageDetail, setPackageDetail] = useState(null);

    const isSkeleton = !products;
    const productsArray = products ?? new Array(5).fill({ isSkeleton: true });

    // Come gli altri "+": primo click chiede conferma, secondo crea il pacchetto,
    // mostra il toast e apre il modale dedicato.
    const handleAddPackage = () => {
        if (confirmType !== 'PACKAGE') {
            setConfirmType('PACKAGE');
            setTimeout(() => setConfirmType(c => (c === 'PACKAGE' ? null : c)), 4000);
            return;
        }
        setConfirmType(null);
        createPackage().then(pkg => {
            toast.success('Pacchetto aggiunto con successo, compilare di seguito i dati');
            setPackageDetail(pkg);
        });
    };

    return (
        <>
            <Header pageTitle={'Gestione servizi'} />
            <Capsule>
                <ProductSection
                    title="principali"
                    type="PRIMARY"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    handleAddProduct={handleAddProduct}
                    confirmType={confirmType}
                    isSkeleton={isSkeleton}
                />
                <ProductSection
                    title="extra"
                    type="EXTRA"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    handleAddProduct={handleAddProduct}
                    confirmType={confirmType}
                    isSkeleton={isSkeleton}
                />
                <ProductSection
                    title="Personal Brand"
                    type="GENERIC"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    handleAddProduct={handleAddProduct}
                    confirmType={confirmType}
                    isSkeleton={isSkeleton}
                />
                <ProductSection
                    title="planimetrie"
                    type="PLAN"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    isSkeleton={isSkeleton}
                />
                <ProductSection
                    title="rendering"
                    type="RENDER"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    isSkeleton={isSkeleton}
                />
                <ProductSection
                    title="credito"
                    type="CREDIT"
                    products={productsArray}
                    onEditProduct={dispatchManageProduct}
                    handleReorderProduct={handleReorderProduct}
                    isSkeleton={isSkeleton}
                />
                {/* Pacchetti: il "+" crea un Product type PACKAGE e apre il modale dedicato */}
                <ProductSection
                    title="pacchetti"
                    type="PACKAGE"
                    products={productsArray}
                    onEditProduct={setPackageDetail}
                    handleReorderProduct={handleReorderProduct}
                    handleAddProduct={handleAddPackage}
                    confirmType={confirmType}
                    isSkeleton={isSkeleton}
                    duplicateIds={duplicatePackageIds}
                />
            </Capsule>
            <OffcanvasManageProduct
                manageProduct={manageProduct}
                handleClose={() => dispatchManageProduct('CLOSE')}
                products={products}
                onUpdateProduct={onUpdateProduct}
            />
            <OffcanvasManagePackage
                packageProduct={packageDetail}
                handleClose={() => setPackageDetail(null)}
                onUpdated={(product) => { onUpdateProduct(product); reloadDuplicatePackages(); }}
                onDeleted={(id) => { removeProductFromList(id); reloadDuplicatePackages(); }}
                products={products}
            />
        </>

    );
}
